import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, MessageCircle, Clock, Calendar, ArrowRight, PhoneCall } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BranchCard = ({ branch, index }) => {
  const { getBranch, t } = useLanguage();
  const localizedBranch = getBranch(branch);
  const primaryPhone = localizedBranch.phone || (localizedBranch.phones && localizedBranch.phones[0]) || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white rounded-2xl border border-gray-200/90 overflow-hidden hover:border-clinical/40 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col h-full group"
    >
      {/* Clinic Image with City Header */}
      <div className="h-52 overflow-hidden bg-gray-100 relative border-b border-gray-100">
        <img 
          src={localizedBranch.image} 
          alt={localizedBranch.name} 
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-black/20 to-transparent"></div>
        
        {/* Top Badges */}
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-3.5 py-1 rounded-full text-xs uppercase tracking-widest font-extrabold text-navy shadow-sm flex items-center space-x-1.5">
          <span className="w-2 h-2 rounded-full bg-teal"></span>
          <span>{localizedBranch.city || localizedBranch.area}</span>
        </div>

        <div className="absolute top-4 right-4 bg-teal/90 text-white backdrop-blur-sm px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center space-x-1 shadow-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          <span>{t('branchesSection.openToday')}</span>
        </div>

        {/* Big Title on bottom overlay */}
        <div className="absolute bottom-3 inset-x-5 text-white">
          <h2 className="text-2xl font-black tracking-wider text-white uppercase drop-shadow-md">
            {localizedBranch.city || localizedBranch.area}
          </h2>
          <p className="text-xs text-teal font-semibold tracking-wide drop-shadow-sm">
            {localizedBranch.name}
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6 md:p-7 flex flex-col flex-grow relative">
        <div className="space-y-4 mb-6 flex-grow">
          {/* Address */}
          <div className="flex items-start space-x-3 text-sm text-gray-600">
            <div className="w-8 h-8 rounded-full border border-blue-50 bg-[#F4F9FF] text-clinical flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
              <MapPin size={16} strokeWidth={2} />
            </div>
            <div className="leading-relaxed font-medium">
              <span className="text-gray-700">{localizedBranch.address}</span>
            </div>
          </div>

          {/* Contact Numbers Row */}
          <div className="flex items-start space-x-3 text-sm text-gray-600 bg-amber-50/70 border border-amber-200/60 rounded-xl p-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center shrink-0">
              <PhoneCall size={16} strokeWidth={2} />
            </div>
            <div>
              <span className="block text-xs uppercase tracking-wider font-bold text-amber-900 mb-0.5">
                {t('branchesSection.contactNo')}
              </span>
              <div className="flex flex-wrap gap-x-2 gap-y-1">
                {localizedBranch.phones && localizedBranch.phones.length > 0 ? (
                  localizedBranch.phones.map((p, idx) => (
                    <a
                      key={idx}
                      href={`tel:${p.replace(/\s+/g, '')}`}
                      className="font-bold text-navy hover:text-clinical hover:underline transition-colors text-sm"
                    >
                      {p.replace('+91 ', '')}
                      {idx < localizedBranch.phones.length - 1 ? ',' : ''}
                    </a>
                  ))
                ) : (
                  <a
                    href={`tel:${primaryPhone.replace(/\s+/g, '')}`}
                    className="font-bold text-navy hover:text-clinical hover:underline transition-colors text-sm"
                  >
                    {localizedBranch.contactNumbers || primaryPhone}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Timings */}
          {localizedBranch.timings && (
            <div className="flex items-center space-x-3 text-xs text-gray-500">
              <div className="w-8 h-8 rounded-full border border-gray-100 bg-white text-teal flex items-center justify-center shrink-0 shadow-xs">
                <Clock size={15} strokeWidth={2} />
              </div>
              <span className="font-medium text-gray-600">{localizedBranch.timings.split('|')[0]}</span>
            </div>
          )}
        </div>

        {/* Buttons & Actions */}
        <div className="pt-4 border-t border-gray-100 mt-auto flex flex-col gap-3">
          {/* Prominent Call Now Button */}
          <a 
            href={`tel:${primaryPhone.replace(/\s+/g, '')}`}
            className="w-full flex items-center justify-center space-x-2 border-2 border-[#6B3A2A] hover:bg-[#6B3A2A] text-[#6B3A2A] hover:text-white py-2.5 rounded-full text-sm font-bold tracking-wide transition-all duration-200 shadow-xs active:scale-98"
          >
            <Phone size={15} />
            <span>{t('hero.callNow')}</span>
          </a>

          {/* Secondary Actions */}
          <div className="grid grid-cols-3 gap-2">
            <a 
              href={`https://wa.me/${localizedBranch.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-1.5 border border-gray-200 hover:border-green-600 hover:bg-green-50 text-gray-700 hover:text-green-600 py-2 rounded-lg text-xs font-semibold transition-all"
              title="Chat on WhatsApp"
            >
              <MessageCircle size={14} className="text-[#25D366]" />
              <span>{t('nav.whatsapp')}</span>
            </a>
            
            <Link 
              to={`/branches/${localizedBranch.slug}`}
              className="flex items-center justify-center space-x-1 border border-gray-200 text-gray-700 hover:text-clinical hover:border-clinical hover:bg-[#F4F9FF] py-2 rounded-lg text-xs font-semibold transition-all group/btn"
              title="View clinic details"
            >
              <span>{t('branchesSection.details')}</span>
              <ArrowRight size={13} className="group-hover/btn:translate-x-0.5 transition-transform" />
            </Link>

            <Link 
              to={`/appointment?branch=${encodeURIComponent(localizedBranch.slug)}`}
              state={{ branch: localizedBranch.slug }}
              className="flex items-center justify-center space-x-1 bg-clinical hover:bg-navy border border-transparent text-white py-2 rounded-lg text-xs font-semibold transition-all shadow-xs shadow-clinical/20"
              title="Book appointment"
            >
              <Calendar size={13} />
              <span>{t('nav.book')}</span>
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BranchCard;
